import { Component, EventEmitter, Injector, Input, OnInit, Output, Inject } from '@angular/core';
import { Company, PolicyDetail, SelectModalInputData } from 'models';
import { ProductsListService } from '../products-list.service';
import { MatDialog } from '@angular/material/dialog';
import { SelectModalComponent } from '../../utilities/select-modal/select-modal.component';

export interface ProductListSelectedFilters {
  companies: string[];
  ramiVita: string[];
  maxRiskGrade: number;
  minPremium: number;
  investTrgt: string;
  suggPeriod: string;
}

@Component({
  selector: 'app-products-list-filters',
  templateUrl: './products-list-filters.component.html',
  styleUrl: './products-list-filters.component.css'
})
export class ProductsListFiltersComponent implements OnInit {
  
  @Inject(ProductsListService) private productList: ProductsListService;
  @Inject(MatDialog) private dialog: MatDialog;
  
  @Input() products: PolicyDetail[] = [];
  @Output() filtersOnChange: EventEmitter<ProductListSelectedFilters> = new EventEmitter();

  isFiltersHidden: boolean = true;

  companyFilters: Company[] = undefined;
  ramoVitaFitlers: string[] = [];
  maxRiskGradeFilters: number[] = [];
  minPremiumFilters: number[] = [];
  investTrgtFilters: string[] = [];
  suggPeriodFilters: string[] = [];

  selectedFilters: ProductListSelectedFilters = {
    companies: [],
    ramiVita: [],
    maxRiskGrade: null,
    minPremium: null,
    investTrgt: null,
    suggPeriod: null
  };

  constructor(
    private injector: Injector
  ) {
    this.productList = this.injector.get(ProductsListService);
    this.dialog = this.injector.get(MatDialog);
  }

  ngOnInit(): void {
    this.loadCompanies();
    this.loadFilters();
  }

  getNActiveFilters(): number {
    let nActiveFilters = 0;
    nActiveFilters += this.selectedFilters.companies?.length ?? 0;
    nActiveFilters += this.selectedFilters.ramiVita?.length ?? 0;
    if (this.selectedFilters.maxRiskGrade != null) { nActiveFilters++; }
    if (this.selectedFilters.minPremium != null) { nActiveFilters++; }
    if (this.selectedFilters.investTrgt != null) { nActiveFilters++; }
    if (this.selectedFilters.suggPeriod != null) { nActiveFilters++; }

    return nActiveFilters
  }

  getToggleTxt(): string {
    const nActiveFilters = this.getNActiveFilters();
    const txtClass = this.isFiltersHidden ? 'pe-1' : 'fw-bold pe-1';
    const txtEnd = nActiveFilters == 1 ? 'o' : 'i';
    const txt = this.isFiltersHidden ? `<i class="fa-solid fa-magnifying-glass"></i> Mostra Filtr${txtEnd}` : `<i class="fa-solid fa-magnifying-glass"></i> Nascondi Filtr${txtEnd}`;
    const icon = this.isFiltersHidden ? 'fa-angle-down gray-color' : 'fa-caret-up';
    const rit = `<p class="show-filters-btn"><span class="${txtClass}">${txt} (${nActiveFilters} attiv${txtEnd}) <i class="fa-solid ${icon}"></i></span></p>`;
    return rit
  }

  openMultipleSelectionModal(selectionOf: ('companies' | 'ramoVita')) {
    const input: SelectModalInputData = {
      header: '',
      selectItems: [],
      isMultipleSelection: true
    }
    
    switch (selectionOf) {

      case 'companies':
        input.header = 'Seleziona le compagnie';
        input.selectItems =  this.companyFilters.map(x => {
          return {
            id: x.FidaCode,
            label: x.Name,
            value: x.FidaCode,
            isSelected: this.selectedFilters.companies.includes(x.FidaCode)
          }
        
        });
        break;

      case 'ramoVita':
        input.header = 'Seleziona i Rami vita';
        input.selectItems =  this.ramoVitaFitlers.map((x, i) => {
          return {
            id: i.toString(),
            label: x,
            value: x,
            isSelected: this.selectedFilters.ramiVita.includes(x)
          }
        });
        break;

    }
    
    const dialogRef = this.dialog.open(SelectModalComponent, {
      width: '400px',
      maxHeight: '600px',
      data: input
    });

    dialogRef.afterClosed().subscribe(
      result => {
        if (result != null) {
          switch (selectionOf) {

            case 'companies':
              this.selectedFilters.companies = result;
            break;

            case 'ramoVita':
            this.selectedFilters.ramiVita = result;
            break;

          }
        }

        this.applyFitler();
      }
    )

  }

  applyFitler() {
    this.filtersOnChange.emit(this.selectedFilters)
  }

  removeAllFilters() {
    this.selectedFilters = {
      companies: [],
      ramiVita: [],
      maxRiskGrade: null,
      minPremium: null,
      investTrgt: null,
      suggPeriod: null
    };
    this.applyFitler();
  }

  loadCompanies() {
    this.companyFilters = this.productList.getCompanies();
    if (this.companyFilters == null || this.companyFilters == undefined) { this.productList.loadCompanies(this.loadCompanies.bind(this)); }
    else {
      const productCompanies: string[] = this.products.map(x => x.SocietyCode.toString());
      this.companyFilters = this.companyFilters.filter(x => productCompanies.includes(x.FidaCode));
      this.companyFilters = this.companyFilters.sort((a, b) => {
        if (a.Name < b.Name) {
          return -1
        }
        if (a.Name > b.Name) {
          return 1
        }
  
        return 0
      });
    }
  }

  loadFilters() {

    if (this.products) {
      this.products.forEach(product => {

        // Filtro Ramo Vita
        if (product.Class != null && !this.ramoVitaFitlers.includes(product.Class)) {
          this.ramoVitaFitlers.push(product.Class)
        }

        // Filtro Max Grado di rischio
        if (product.RiskGradeMax != null && !this.maxRiskGradeFilters.includes(product.RiskGradeMax)) {
          this.maxRiskGradeFilters.push(product.RiskGradeMax);
        }

        // Filtro Premio minimo
        if (product.MinSinglePremium != null && product.MinSinglePremium > 0 && !this.minPremiumFilters.includes(product.MinSinglePremium)) {
          this.minPremiumFilters.push(product.MinSinglePremium);
        }

        // Filtro Obiettivo investimento
        if (product.InvestTrgt != null && product.InvestTrgt !== '-' && !this.investTrgtFilters.includes(product.InvestTrgt)) {
          this.investTrgtFilters.push(product.InvestTrgt);
        }

        // Filtro Durata raccomandata
        if (product.SuggestedPeriod != null && product.SuggestedPeriod != '' && !this.suggPeriodFilters.includes(product.SuggestedPeriod)) {
          this.suggPeriodFilters.push(product.SuggestedPeriod);
        }
      })

      this.ramoVitaFitlers = this.sortFilter(this.ramoVitaFitlers) as string[];
      this.maxRiskGradeFilters = this.sortFilter(this.maxRiskGradeFilters) as number[];
      this.minPremiumFilters = this.sortFilter(this.minPremiumFilters) as number[];
      this.investTrgtFilters = this.sortFilter(this.investTrgtFilters) as string[];
      this.suggPeriodFilters = this.sortFilter(this.suggPeriodFilters.map(x => parseInt(x))) as string[];
    }
  }

  sortFilter(items: string[] | number[]): string[] | number[] {
    return items.sort((a, b) => {
      if (a < b) {
        return -1
      }
      if (a > b) {
        return 1
      }

      return 0
    })
  }

}
